import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  useGetActivitiesPageQuery,
  useGetMoviesPageQuery,
  useGetRoomsPageQuery,
} from '@/store/api/ticketingApi';

export type CinemaSection = 'activities' | 'movies' | 'rooms' | 'seat-maps' | 'showtimes';

export interface ReferenceOption {
  id: string;
  label: string;
}

/**
 * The page endpoints cap `limit` at 100 and are cursor-paginated. A showtime
 * form needs the full reference list to offer a picker, so this reads the
 * first page at the maximum size and says so honestly when more exist rather
 * than silently truncating the choices.
 */
const REFERENCE_LIMIT = 100;

interface ReferenceListState {
  options: ReferenceOption[];
  isLoading: boolean;
  hasMore: boolean;
  failed: boolean;
}

function useReferenceOptions(
  kind: 'activity' | 'movie' | 'room',
  enabled: boolean,
): ReferenceListState {
  const query = { limit: REFERENCE_LIMIT };
  const skip = !enabled;
  const activities = useGetActivitiesPageQuery(query, { skip: skip || kind !== 'activity' });
  const movies = useGetMoviesPageQuery(query, { skip: skip || kind !== 'movie' });
  const rooms = useGetRoomsPageQuery(query, { skip: skip || kind !== 'room' });

  const active = kind === 'activity' ? activities : kind === 'movie' ? movies : rooms;
  const page = active.currentData;

  let options: ReferenceOption[] = [];
  if (kind === 'activity') {
    options = (activities.currentData?.data ?? []).map((row) => ({
      id: row.id,
      label: `${row.name} (${row.kind})`,
    }));
  } else if (kind === 'movie') {
    options = (movies.currentData?.data ?? []).map((row) => ({ id: row.id, label: row.title }));
  } else {
    options = (rooms.currentData?.data ?? []).map((row) => ({
      id: row.id,
      label: row.capacity === null ? row.name : `${row.name} · ${row.capacity} seats`,
    }));
  }

  return {
    options,
    isLoading: active.isLoading,
    hasMore: Boolean(page?.pagination.hasMore),
    failed: active.isError,
  };
}

/**
 * Dropdown over a real reference list, so an operator picks a record instead
 * of pasting an ID. The current value is ALWAYS rendered even when the list
 * failed to load or no longer contains it — silently dropping a linked record
 * would rewrite the showtime on save.
 */
export function ReferenceSelect({
  id,
  label,
  value,
  onChange,
  kind,
  required,
  disabled,
  placeholder,
  emptyLabel,
  help,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  kind: 'activity' | 'movie' | 'room';
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  help?: string;
}) {
  const { options, isLoading, hasMore, failed } = useReferenceOptions(kind, !disabled);
  const known = options.some((option) => option.id === value);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        required={required}
        disabled={disabled || isLoading}
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{emptyLabel ?? placeholder}</option>
        {/* Keep an unknown current value selectable instead of blanking it. */}
        {value !== '' && !known && <option value={value}>{value} (not in the current list)</option>}
        {options.map((option) => (
          <option key={option.id} value={option.id}>{option.label}</option>
        ))}
      </select>
      {help && <span className="text-xs text-muted-foreground">{help}</span>}
      {failed && (
        <span role="alert" className="text-xs text-destructive">
          Could not load the {kind} list. Existing IDs are preserved; retry by reopening this form.
        </span>
      )}
      {hasMore && (
        <span className="text-xs text-muted-foreground">
          Showing the first {options.length} {kind} records. Create via the {kind} page if the one you need is missing.
        </span>
      )}
    </div>
  );
}

/**
 * Single route pattern for every Cinema surface: list, create (`/new`), detail.
 *
 * The section pages decide `createMode` from `useParams().id === 'new'`, so the
 * `:id` segment must be optional here — a dedicated `/cinema/:section/new`
 * route has no `:id` param and silently falls back to rendering the list.
 */
export const CINEMA_ROUTE_PATH = '/cinema/:section/:id?';

const destinations: Array<{ id: CinemaSection; label: string }> = [
  { id: 'activities', label: 'Activities' },
  { id: 'movies', label: 'Movies' },
  { id: 'rooms', label: 'Rooms' },
  { id: 'seat-maps', label: 'Seat maps' },
  { id: 'showtimes', label: 'Showtimes' },
];

export function CinemaNavigation({ active }: { active: CinemaSection }) {
  return (
    <nav aria-label="Cinema management" className="flex flex-wrap gap-2 border-b pb-3">
      {destinations.map(({ id, label }) => (
        <Button key={id} asChild variant={active === id ? 'secondary' : 'ghost'}>
          <Link to={`/cinema/${id}`} aria-current={active === id ? 'page' : undefined}>{label}</Link>
        </Button>
      ))}
    </nav>
  );
}

export function CinemaLoadError({ label, retry }: { label: string; retry: () => void }) {
  return (
    <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
      Could not load {label}. <Button type="button" variant="outline" onClick={retry}>Try again</Button>
    </div>
  );
}

export function parseJsonObject(value: string): Record<string, unknown> | null {
  const parsed: unknown = JSON.parse(value);
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Enter a JSON object.');
  }
  return parsed as Record<string, unknown>;
}

export function parseSeatMapLayout(value: string) {
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || !Array.isArray((parsed as { rows?: unknown }).rows)) {
    throw new Error('Layout must be an object with a rows array.');
  }
  const rows = (parsed as { rows: unknown[] }).rows.map((candidate) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) throw new Error('Each layout row must be an object.');
    const row = candidate as Record<string, unknown>;
    if (!Number.isSafeInteger(row.row) || !Array.isArray(row.seats)) throw new Error('Each row needs an integer row number and a seats array.');
    const seats = row.seats.map((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) throw new Error('Each seat needs a column and label.');
      const seat = entry as Record<string, unknown>;
      if (!Number.isSafeInteger(seat.col) || typeof seat.label !== 'string' || !seat.label.trim()) {
        throw new Error('Each seat needs an integer column and non-empty label.');
      }
      if (seat.kind !== undefined && !['standard', 'vip', 'accessible', 'loveseat'].includes(String(seat.kind))) {
        throw new Error('Seat kind must be standard, vip, accessible, or loveseat.');
      }
      return { col: seat.col as number, label: seat.label, ...(seat.kind ? { kind: seat.kind as import('@/store/api/ticketingApi').SeatLayoutEntry['kind'] } : {}) };
    });
    const gaps = row.gaps === undefined ? [] : row.gaps;
    const labels = row.labels === undefined ? [] : row.labels;
    if (!Array.isArray(gaps) || !gaps.every((column) => Number.isSafeInteger(column))) throw new Error('Row gaps must be integer columns.');
    if (!Array.isArray(labels) || !labels.every((label) => typeof label === 'string')) throw new Error('Row labels must be strings.');
    return { row: row.row as number, seats, gaps: gaps as number[], labels: labels as string[] };
  });
  return { rows } as import('@/store/api/ticketingApi').SeatMapLayout;
}

export function localDateTimeValue(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function formatCinemaDate(value?: string | null): string {
  if (!value) return 'Not provided';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Date unavailable'
    : `${new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date)} (${new Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time'})`;
}

export function PageControls({
  hasMore,
  loading,
  onMore,
}: {
  hasMore: boolean;
  loading: boolean;
  onMore: () => void;
}) {
  if (!hasMore) return null;
  return <Button type="button" variant="outline" disabled={loading} onClick={onMore}>{loading ? 'Loading…' : 'Load more'}</Button>;
}
